import { AgentLinkResponse, type MySample, MySamplesResponse } from '@tabel/shared/schemas/agent-data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, TriangleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ApiError, reportClientError } from '../lib/errors';
import { ruDateTime } from '../lib/format';
import { type AppRole, contextHeaders, useRole } from '../lib/role';
import { ErrorCodes, errorCode, errorSentence } from './ErrorToast';
import { Button, Modal, Skeleton, fieldCls } from './ui';

/*
 * «Передать день агенту» (005, T138; spec.md FR-064, US6 сценарий 7; contracts/http-api.md
 * `POST /api/days/:personId/:date/agent-link`) — в меню «…» карточки дня и в подвале «Открыть расчёт дня» страницы
 * правил (`pages/Rules/DayPanel.tsx`): там роли строки «Выборки дней» видят реальный день. Нажатие создаёт выборку из
 * одного дня — тот же день, что агент получил бы выборкой (FR-024): под меткой, без ФИО, табельного номера и
 * комментариев. Ответ — ссылка (короткий текст для разговора с агентом), срок и номер выборки. Ссылка действует 24 часа
 * и только для автора (FR-027). Формы ответов — общие схемы `@tabel/shared/schemas/agent-data`.
 * - Действие видно, только если роль из строки «Выборки дней» таблицы прав (правило `day.agentLink`,
 *   `auth/permissions.ts`) и сервер ответил `canShareDay: true` в «Моих выборках» (`GET /api/me/samples`): выборки на
 *   объекте включены и у пользователя есть право «ИИ-агент» (FR-063). Другим ролям «Мои выборки» не запрашиваются.
 *   Отказ, сбой или ответ не по схеме — действия нет. Окончательно право проверяет сервер: отказ — в окне с кодом.
 * - «Мои выборки» — один запрос и один ключ кэша (`mySamplesKey`) для карточки дня, «Открыть расчёт дня» и страницы
 *   «Мои ИИ-клиенты». Новая ссылка — новая выборка: после неё «Мои выборки» загружаются заново.
 * - Окно: заглушка, пока ссылка создаётся; ссылка в поле только для чтения и «Скопировать»; итог копирования —
 *   строкой на месте (тост под окном не виден). Буфер обмена недоступен — ссылка выделена для копирования вручную.
 * - Отказ сервера — его слова и код рядом с кодом ошибки (итоговое ревью T160). Суточный лимит (`429 RATE_LIMITED`,
 *   `details.retryAfterSec`) — ещё когда лимит обновится, по времени объекта; «Повторить» до этого ничего не даст — его нет.
 * - Сбой связи, сбой сервера или ответ не по схеме: ответ мог потеряться, а выборка — уже записана. Окно не создаёт
 *   вторую выборку молча: сначала перечитывает «Мои выборки». Новая выборка «Передать день агенту» с номером больше, чем
 *   до нажатия, — окно говорит, что ссылка создана (саму ссылку показать нельзя: сервер хранит только отпечаток её
 *   секрета), и новую ссылку создаёт только по кнопке. Новой выборки нет — «Ссылка не создана. Создать ссылку ещё
 *   раз?». «Мои выборки» не загрузились — «Проверить снова» перечитывает их, а не создаёт ссылку.
 */

/** Роли строки «Выборки дней» таблицы прав 005 и правила `day.agentLink`: администратор правил, утверждающий, проверяющий. */
export const AGENT_LINK_ROLES: readonly AppRole[] = ['rules_admin', 'approver', 'auditor'];

/** Подписи — до 6 слов. Название окна совпадает с пунктом меню. */
export const AGENT_LINK_LABEL = {
  menu: 'Передать день агенту',
  title: 'Передать день агенту',
  link: 'Ссылка для агента',
  copy: 'Скопировать',
  copied: 'Скопировано',
  done: 'Готово',
  retry: 'Повторить',
  close: 'Закрыть',
  creating: 'Создание ссылки…',
  checking: 'Проверка ссылки…',
  createAgain: 'Создать ещё раз',
  createNew: 'Создать новую ссылку',
  recheck: 'Проверить снова',
  errorCode: 'Код ошибки:',
} as const;

/** Предложения окна — ASD-STE100: короткие, условие первым, без слов разработчика. */
export const AGENT_LINK_TEXT = {
  what: 'Агент получит этот день под меткой: проходы, расчёт и итоги. ФИО, табельного номера и комментариев в нём нет.',
  how: 'Скопируйте ссылку и вставьте её в разговор с ИИ-агентом.',
  copied: 'Ссылка скопирована. Вставьте её в разговор с агентом.',
  copyFailed: 'Ссылка не скопирована. Она выделена: скопируйте её вручную.',
  failed: 'Ссылка не создана',
  /** Сбой связи, а в «Моих выборках» новой выборки нет. */
  askAgain: 'Создать ссылку ещё раз?',
  /** Сбой связи, а в «Моих выборках» появилась новая выборка. */
  foundNote:
    'Эту ссылку показать нельзя: её видно только сразу после создания. Если ссылка нужна, создайте новую.',
  /** Сбой связи, и «Мои выборки» не загрузились. */
  unchecked: 'Нет связи с сервером. Система не смогла проверить, создана ли ссылка.',
} as const;

/** Тексты с подстановкой: `when` — дата и время по объекту, `id` — номер выборки с сервера. */
export const sayAgentLink = {
  /** Подзаголовок окна: какой день передаётся. ФИО видит только сам пользователь, агенту оно не уходит. */
  subtitle: (name: string, day: string) => `${name} · ${day}`,
  expires: (when: string) => `Ссылка действует 24 часа, до ${when}. Она работает только для вас.`,
  sample: (id: number) => `Выборка № ${id} записана в журнал действий.`,
  /** `429 RATE_LIMITED`: когда обновится суточный лимит дней (FR-023, US6 сценарий 4). */
  limitReset: (when: string) => `Лимит дней для агента обновится ${when}.`,
  /** Сбой связи после нажатия, а выборка записана: номер и время из «Моих выборок». */
  found: (id: number, when: string) =>
    `Связь с сервером прервалась, но ссылка создана: выборка № ${id} от ${when}.`,
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

/** Ответ не по схеме (например, HTML вместо JSON) — сбой сервера с кодом ошибки. */
const badAnswer = (status: number, requestId: string | null) =>
  new ApiError(status, 'INTERNAL', 'Ошибка сервера', requestId);

/** Запрос в роли и объекте интерфейса с сессией; ошибка — тело 004 `{ error: { code, message, requestId } }`. */
async function call(method: 'GET' | 'POST', url: string): Promise<{ data: unknown; res: Response }> {
  let res: Response;
  try {
    // Тела нет: без `Content-Type`. `Origin` браузер ставит сам, сервер его проверяет (004).
    res = await fetch(url, { method, credentials: 'include', headers: contextHeaders() });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const e = isObject(data) && isObject(data.error) ? data.error : {};
    const code = isText(e.code)
      ? (e.code as ApiError['code'])
      : res.status === 404
        ? 'NOT_FOUND'
        : 'INTERNAL';
    // Сессия кончилась — страница входа, как у остальных запросов (`lib/api.ts`).
    if (res.status === 401 && code === 'UNAUTHENTICATED') location.assign('/login');
    const details = isObject(e.details) ? e.details : undefined;
    throw new ApiError(
      res.status,
      code,
      isText(e.message) ? e.message : `Ошибка ${res.status}`,
      isText(e.requestId) ? e.requestId : requestId,
      typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : undefined, // `RATE_LIMITED`
      details,
    );
  }
  return { data, res };
}

/** Ключ кэша «Моих выборок»: ответ зависит от роли (право `me.samples`), смена роли — запрос заново. */
export const mySamplesKey = (role: AppRole) => ['my-samples', role] as const;

/**
 * «Мои выборки» (`GET /api/me/samples`): свои выборки, новые сверху, и `canShareDay`. Ответ — по схеме
 * `MySamplesResponse`: лишние поля (люди, метки, автор) дальше не идут; не по схеме — ошибка, а не пустой список.
 */
export async function requestMySamples(): Promise<MySamplesResponse> {
  const { data, res } = await call('GET', '/api/me/samples');
  const parsed = MySamplesResponse.safeParse(data);
  if (!parsed.success) throw badAnswer(res.status, res.headers.get('x-request-id'));
  return parsed.data;
}

/** Создать ссылку на день — выборку из одного дня. Тела нет: день — в адресе. Ответ — по схеме `AgentLinkResponse`. */
export async function requestDayAgentLink(personId: string, date: string): Promise<AgentLinkResponse> {
  const url = `/api/days/${encodeURIComponent(personId)}/${encodeURIComponent(date)}/agent-link`;
  const { data, res } = await call('POST', url);
  const parsed = AgentLinkResponse.safeParse(data);
  if (!parsed.success || parsed.data.link.trim() === '')
    throw badAnswer(res.status, res.headers.get('x-request-id'));
  return parsed.data;
}

/** Сбой связи, сервера или ответ не по схеме — в серверный журнал (принцип II); отказ по правам — нет. */
const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;

/**
 * Исход создания ссылки неизвестен: связь оборвалась, сервер упал или ответил не по схеме — выборка могла записаться.
 * Отказ по правам, лимиту или разрешению — исход известен: выборки нет.
 */
const outcomeUnknown = shouldReport;

/** Наибольший номер своих выборок — до нажатия: новая выборка получит номер больше. */
const lastSampleId = (data: MySamplesResponse | undefined) =>
  Math.max(0, ...(data?.samples ?? []).map((s) => s.id));

/** Новая выборка «Передать день агенту» после нажатия: номер больше, чем у выборок до нажатия; нет — `null`. */
function newDayLink(data: MySamplesResponse, before: number): MySample | null {
  const fresh = data.samples.filter((s) => s.source === 'day_link' && s.id > before);
  return fresh.sort((a, b) => b.id - a.id)[0] ?? null;
}

/** «Мои выборки» не устаревают 30 с: перелистывание дней в панели не повторяет запрос. */
const SAMPLES_STALE_MS = 30_000;

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * «Передать день агенту»: `allowed` — показать действие, `start` — создать ссылку и открыть окно, `dialog` — окно
 * (ставится рядом с панелью). `role` — роль карточки; нет — роль интерфейса. `screen` — экран для журнала ошибок.
 */
export function useDayAgentLink(o: {
  role?: AppRole;
  personId: string;
  date: string;
  /** Панель открыта: закрыли — окно тоже закрывается; закрытая панель «Мои выборки» не запрашивает. */
  open: boolean;
  tzOffset: string;
  /** Подзаголовок окна: человек и дата. */
  subtitle: string;
  screen?: string;
}): { allowed: boolean; start: () => void; dialog: ReactNode } {
  const ctx = useRole();
  const role = o.role ?? ctx.role;
  const screen = o.screen ?? 'Timesheet';
  const qc = useQueryClient();
  const fits = AGENT_LINK_ROLES.includes(role) && o.date !== '';
  const samples = useQuery({
    queryKey: mySamplesKey(role),
    queryFn: requestMySamples,
    enabled: o.open && fits,
    staleTime: SAMPLES_STALE_MS,
  });
  const allowed = fits && samples.data?.canShareDay === true;
  // Сбой или ответ не по схеме: действия нет, а ошибка — в серверный журнал (отказ по правам — нет).
  useEffect(() => {
    if (samples.error && shouldReport(samples.error))
      reportClientError(screen, 'load samples', samples.error);
  }, [samples.error, screen]);
  const [shown, setShown] = useState(false);
  /** Наибольший номер своих выборок до нажатия: по нему видно, записалась ли выборка, если ответ потерялся. */
  const before = useRef(0);
  /** Когда пришёл отказ: от него считается, когда обновится суточный лимит. */
  const [failedAt, setFailedAt] = useState(0);
  // Исход неизвестен — «Мои выборки» заново, без кэша. Сбой этой загрузки журналирует запрос «Моих выборок» выше.
  const check = useMutation({
    mutationFn: async () =>
      newDayLink(
        await qc.fetchQuery({ queryKey: mySamplesKey(role), queryFn: requestMySamples, staleTime: 0 }),
        before.current,
      ),
  });
  const create = useMutation({
    mutationFn: () => requestDayAgentLink(o.personId, o.date),
    onError: (e) => {
      setFailedAt(Date.now());
      if (shouldReport(e)) reportClientError(screen, 'day agent link', e);
      if (outcomeUnknown(e)) check.mutate();
    },
    // Новая выборка или отказ (выборки выключили, право сняли) — «Мои выборки» и признак кнопки заново. Исход
    // неизвестен — «Мои выборки» перечитывает проверка.
    onSettled: (_data, e) => {
      if (!e || !outcomeUnknown(e)) void qc.invalidateQueries({ queryKey: mySamplesKey(role) });
    },
  });
  const reset = create.reset;
  const resetCheck = check.reset;

  useEffect(() => {
    if (!o.open) setShown(false);
  }, [o.open]);

  /** Создать ссылку: запомнить последнюю выборку до нажатия и отправить запрос. */
  const send = () => {
    resetCheck();
    before.current = lastSampleId(qc.getQueryData<MySamplesResponse>(mySamplesKey(role)) ?? samples.data);
    create.mutate();
  };
  const start = () => {
    reset();
    setShown(true);
    send();
  };
  const close = () => {
    setShown(false);
    reset();
    resetCheck();
  };

  const unknown = create.isError && outcomeUnknown(create.error);
  return {
    allowed,
    start,
    dialog: (
      <Modal open={shown} onClose={close} title={AGENT_LINK_LABEL.title} description={o.subtitle}>
        {create.data ? (
          <LinkReady data={create.data} tzOffset={o.tzOffset} onDone={close} />
        ) : unknown && check.isSuccess && check.data ? (
          <LinkFound sample={check.data} tzOffset={o.tzOffset} onCreate={send} onClose={close} />
        ) : unknown && check.isSuccess ? (
          <LinkError error={create.error} ask onRetry={send} onClose={close} />
        ) : unknown && check.isError ? (
          <LinkUnchecked onRecheck={() => check.mutate()} onClose={close} />
        ) : unknown ? (
          <Creating label={AGENT_LINK_LABEL.checking} />
        ) : create.isError ? (
          <LinkError
            error={create.error}
            limitReset={limitResetOf(create.error, failedAt, o.tzOffset)}
            onRetry={send}
            onClose={close}
          />
        ) : (
          <Creating label={AGENT_LINK_LABEL.creating} />
        )}
      </Modal>
    ),
  };
}

/** `429 RATE_LIMITED`: когда обновится суточный лимит — время отказа плюс `details.retryAfterSec`, по времени объекта. */
function limitResetOf(error: unknown, failedAt: number, tzOffset: string): string | null {
  if (!(error instanceof ApiError) || error.code !== 'RATE_LIMITED') return null;
  const sec = error.retryAfterSec;
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec <= 0) return null;
  return ruDateTime(new Date(failedAt + sec * 1000).toISOString(), tzOffset);
}

/** Ссылка готова: что получит агент, поле со ссылкой и «Скопировать», как вставить, срок, номер выборки. */
function LinkReady({
  data,
  tzOffset,
  onDone,
}: { data: AgentLinkResponse; tzOffset: string; onDone: () => void }) {
  const id = useId();
  const field = useRef<HTMLInputElement>(null);
  const [copy, setCopy] = useState<CopyState>('idle');
  const copyLink = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('буфер обмена недоступен');
      await navigator.clipboard.writeText(data.link);
      setCopy('copied');
    } catch {
      // Буфер обмена запрещён или его нет: ссылка выделена, её копируют вручную.
      setCopy('failed');
      field.current?.focus();
      field.current?.select();
    }
  };
  const copied = copy === 'copied';
  return (
    <div className="space-y-4 text-[13.5px] text-ink">
      <p>{AGENT_LINK_TEXT.what}</p>
      <div>
        <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink">
          {AGENT_LINK_LABEL.link}
        </label>
        <div className="flex items-center gap-2 max-sm:flex-col max-sm:items-stretch">
          <input
            ref={field}
            id={id}
            readOnly
            value={data.link}
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
            className={`${fieldCls} w-full min-w-0 flex-1 font-mono`}
          />
          <Button variant="primary" icon={copied ? Check : Copy} onClick={() => void copyLink()}>
            {copied ? AGENT_LINK_LABEL.copied : AGENT_LINK_LABEL.copy}
          </Button>
        </div>
        <p
          role="status"
          className={`mt-1.5 min-h-[18px] text-[12.5px] ${copy === 'failed' ? 'text-warn' : 'text-ok'}`}
        >
          {copy === 'copied' ? AGENT_LINK_TEXT.copied : copy === 'failed' ? AGENT_LINK_TEXT.copyFailed : ''}
        </p>
      </div>
      <p>{AGENT_LINK_TEXT.how}</p>
      <p className="text-ink-2">{sayAgentLink.expires(ruDateTime(data.expiresAt, tzOffset))}</p>
      <p className="text-[12.5px] text-ink-3">{sayAgentLink.sample(data.sampleId)}</p>
      <div className="flex justify-end pt-1 max-sm:[&>button]:flex-auto">
        <Button onClick={onDone}>{AGENT_LINK_LABEL.done}</Button>
      </div>
    </div>
  );
}

/** Заглушка: ссылка создаётся или окно проверяет «Мои выборки» после сбоя. */
function Creating({ label }: { label: string }) {
  return (
    <div aria-busy="true" aria-label={label} className="space-y-3">
      <p className="text-[13.5px] text-ink-2">{label}</p>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-4 w-3/5" />
    </div>
  );
}

const ACTIONS = 'flex flex-wrap justify-end gap-2 max-sm:[&>button]:flex-auto';
const BOX = 'flex items-start gap-2.5 rounded-control px-3 py-2.5 text-[13px]';

/**
 * Ссылка не создана: причина словами сервера или текстом кода, код ошибки для команды разработки. Отказ — «Повторить»;
 * у суточного лимита — когда он обновится, без «Повторить». После сбоя связи (`ask`) новой выборки в «Моих выборках»
 * нет — вопрос «Создать ссылку ещё раз?» и «Создать ещё раз».
 */
function LinkError({
  error,
  ask = false,
  limitReset = null,
  onRetry,
  onClose,
}: {
  error: unknown;
  ask?: boolean;
  limitReset?: string | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <div className="space-y-4">
      <div role="alert" className={`${BOX} bg-bad-soft text-bad`}>
        <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium">{AGENT_LINK_TEXT.failed}.</p>
          <p>{errorSentence(error)}</p>
          {limitReset && <p>{sayAgentLink.limitReset(limitReset)}</p>}
          {(requestId || code) && (
            <p className="mt-0.5 text-[12px]">
              {AGENT_LINK_LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
            </p>
          )}
        </div>
      </div>
      {ask && <p className="text-[13.5px] text-ink">{AGENT_LINK_TEXT.askAgain}</p>}
      <div className={ACTIONS}>
        <Button onClick={onClose}>{AGENT_LINK_LABEL.close}</Button>
        {!limitReset && (
          <Button variant="primary" onClick={onRetry}>
            {ask ? AGENT_LINK_LABEL.createAgain : AGENT_LINK_LABEL.retry}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Сбой связи, а в «Моих выборках» новая выборка «Передать день агенту»: ссылка создана. Показать её нельзя — сервер
 * хранит только отпечаток секрета ссылки. Новая ссылка — только по кнопке: это ещё одна выборка в журнале и в лимите.
 */
function LinkFound({
  sample,
  tzOffset,
  onCreate,
  onClose,
}: { sample: MySample; tzOffset: string; onCreate: () => void; onClose: () => void }) {
  return (
    <div className="space-y-4">
      <div role="status" className={`${BOX} bg-warn-soft text-warn`}>
        <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p className="font-medium">
            {sayAgentLink.found(sample.id, ruDateTime(sample.createdAt, tzOffset))}
          </p>
          <p className="text-ink-2">{AGENT_LINK_TEXT.foundNote}</p>
        </div>
      </div>
      <div className={ACTIONS}>
        <Button onClick={onClose}>{AGENT_LINK_LABEL.close}</Button>
        <Button variant="primary" onClick={onCreate}>
          {AGENT_LINK_LABEL.createNew}
        </Button>
      </div>
    </div>
  );
}

/** Сбой связи, и «Мои выборки» не загрузились: не видно, создана ли ссылка. «Проверить снова» ссылку не создаёт. */
function LinkUnchecked({ onRecheck, onClose }: { onRecheck: () => void; onClose: () => void }) {
  return (
    <div className="space-y-4">
      <div role="alert" className={`${BOX} bg-bad-soft text-bad`}>
        <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
        <p className="min-w-0">{AGENT_LINK_TEXT.unchecked}</p>
      </div>
      <div className={ACTIONS}>
        <Button onClick={onClose}>{AGENT_LINK_LABEL.close}</Button>
        <Button variant="primary" onClick={onRecheck}>
          {AGENT_LINK_LABEL.recheck}
        </Button>
      </div>
    </div>
  );
}
